import type { Bulletin } from '@shared/types/Bulletin';
import type { Salarie } from '@shared/types/Salarie';
import type { Tenant } from '@shared/types/Tenant';
import { TAUX_CNAMGS, TAUX_CNAMGS_EMP, TAUX_CNAMGS_SAL } from '@shared/constants/Taux';
import { retenirBulletinsUniques } from '@domain/paie/BulletinsUniques';
import { roundFCFA } from '@shared/utils/round';

const PERIODE_RE = /^(\d{4})-(\d{2})$/;

export interface LigneCnamgsMensuelle {
  salarie_id: string;
  matricule: string;
  nom_complet: string;
  numero_cnamgs: string;
  base: number;
  patronale: number;
  salariale: number;
}

export interface EtatCnamgsMensuel {
  periode: string;
  tenant_id: string;
  lignes: LigneCnamgsMensuelle[];
  total_base: number;
  total_patronale: number;
  total_salariale: number;
  effectif: number;
  taux_patronal: number;
  taux_salarial: number;
  /** Échéance cotisations : 20 du mois suivant (réf. EcheancesFiscalesGabon). */
  date_limite: string;
}

function bulletinEligible(b: Bulletin): boolean {
  return b.statut === 'valide' || b.statut === 'paye';
}

function echapperCsv(value: string): string {
  return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Déclaration mensuelle CNAMGS (FISC-B-03).
 * État des cotisations salariales (2 %) et patronales (4,1 %) par entreprise,
 * à partir des bulletins validés/payés du mois.
 */
export class DeclarationCnamgsMensuelleService {
  static dateLimite(periode: string): string {
    const m = PERIODE_RE.exec(periode);
    if (!m) throw new Error('Période invalide (attendu YYYY-MM)');
    const annee = Number(m[1]);
    const mois = Number(m[2]);
    if (mois < 1 || mois > 12) throw new Error('Période invalide (mois 01–12)');
    const next = new Date(annee, mois, 20);
    return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-20`;
  }

  static genererEtat(
    bulletins: readonly Bulletin[],
    salaries: readonly Salarie[],
    periode: string,
    tenant?: Tenant | null,
  ): EtatCnamgsMensuel {
    if (!PERIODE_RE.test(periode)) {
      throw new Error('Période invalide (attendu YYYY-MM)');
    }
    const parId = new Map(salaries.map((s) => [s.id, s]));
    // Un seul bulletin par salarié et par période : des doublons historiques ne gonflent ni l'effectif ni les montants.
    const retenus = retenirBulletinsUniques(
      bulletins.filter((b) => b.periode === periode && bulletinEligible(b)),
    );
    const lignes: LigneCnamgsMensuelle[] = retenus
      .map((b) => {
        const sal = parId.get(b.salarie_id);
        const nom = b.nom_complet || (sal ? `${sal.prenom} ${sal.nom}` : '') || b.matricule || '—';
        return {
          salarie_id: b.salarie_id,
          matricule: b.matricule || sal?.matricule || '',
          nom_complet: nom,
          numero_cnamgs: sal?.cnamgs || '',
          // Plafond CNAMGS par salarié et par mois (décision du 16/10/2026).
          base: roundFCFA(Math.min(b.base_cnamgs || 0, TAUX_CNAMGS.PLAFOND_MENSUEL)),
          patronale: roundFCFA((b.cnamgs_emp || 0) + (b.cnamgs_fam_emp || 0)),
          salariale: roundFCFA(b.cnamgs_sal || 0),
        };
      })
      .sort((a, b) => a.matricule.localeCompare(b.matricule, 'fr'));

    return {
      periode,
      tenant_id: tenant?.id ?? '',
      lignes,
      total_base: lignes.reduce((s, l) => s + l.base, 0),
      total_patronale: lignes.reduce((s, l) => s + l.patronale, 0),
      total_salariale: lignes.reduce((s, l) => s + l.salariale, 0),
      effectif: lignes.length,
      taux_patronal: TAUX_CNAMGS_EMP,
      taux_salarial: TAUX_CNAMGS_SAL,
      date_limite: this.dateLimite(periode),
    };
  }

  static exporterCsv(etat: EtatCnamgsMensuel, entreprise?: Tenant | null): string {
    const sep = ';';
    const paires: Array<[string, string]> = [
      ['Déclaration', 'CNAMGS mensuelle'],
      ['Période', etat.periode],
      ['Employeur', entreprise?.raison_sociale || entreprise?.nom || ''],
      ['NIF', entreprise?.nif || ''],
      ['N° CNAMGS employeur', entreprise?.parametres?.numero_cnamgs_employeur || ''],
      ['Taux patronal', `${(etat.taux_patronal * 100).toFixed(1)} %`],
      ['Taux salarial', `${(etat.taux_salarial * 100).toFixed(1)} %`],
      ['Date limite', etat.date_limite],
      ['Effectif déclaré', String(etat.effectif)],
    ];
    const colonnes = ['Matricule', 'Salarié', 'N° CNAMGS', 'Base', 'Patronale', 'Salariale'];
    const lignes = [
      ...paires.map(([cle, valeur]) => [cle, echapperCsv(valeur)].join(sep)),
      '',
      colonnes.join(sep),
    ];
    for (const l of etat.lignes) {
      lignes.push(
        [
          echapperCsv(l.matricule),
          echapperCsv(l.nom_complet),
          echapperCsv(l.numero_cnamgs),
          l.base,
          l.patronale,
          l.salariale,
        ].join(sep),
      );
    }
    if (etat.lignes.length > 0) {
      lignes.push(
        ['TOTAL', '', '', etat.total_base, etat.total_patronale, etat.total_salariale].join(sep),
      );
    }
    return lignes.join('\n');
  }

  static nomFichier(periode: string): string {
    return `declaration_cnamgs_mensuelle_${periode}.csv`;
  }
}
